import { styled } from "goober";
import {
  type ComponentProps,
  type MouseEventHandler,
  type ReactNode,
  type RefObject,
  useState,
} from "react";
import type { GraphNode } from "./buildAccessModel";
import { GraphEdges, HoverEdges } from "./GraphEdges";
import { GraphNodeChip, type ChipState } from "./GraphNodeChip";
import { intersects, nodeBounds } from "./graphViewport";
import { useGraphViewport } from "./useGraphViewport";

const Surface = styled("div")`
  position: relative;
  z-index: 0;
  margin: 24px 24px 56px;
`;

interface Props extends Omit<ComponentProps<typeof GraphEdges>, "viewport"> {
  nodes: readonly GraphNode[];
  visualById: ReadonlyMap<string, ReactNode>;
  scrollRef: RefObject<HTMLDivElement | null>;
  canvasRef: RefObject<HTMLDivElement | null>;
  pinnedIds: readonly (string | null | undefined)[];
  hoverActive: boolean;
  nodeState: (node: GraphNode) => ChipState;
  onPointerDown: ComponentProps<typeof GraphNodeChip>["onPointerDown"];
  onMore: ComponentProps<typeof GraphNodeChip>["onMore"];
  onBackground: MouseEventHandler<HTMLDivElement>;
}

export function GraphSurface({
  nodes,
  visualById,
  scrollRef,
  canvasRef,
  pinnedIds,
  hoverActive,
  nodeState,
  onPointerDown,
  onMore,
  onBackground,
  ...edgeProps
}: Props) {
  const viewport = useGraphViewport({ scrollRef, canvasRef });
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const mountedNodes = nodes.filter((node) => {
    const position = edgeProps.positions.get(node.id);
    return (
      position &&
      (node.id === focusedId ||
        pinnedIds.includes(node.id) ||
        intersects(nodeBounds(position), viewport))
    );
  });
  return (
    <Surface
      ref={canvasRef}
      style={{ width: edgeProps.width, height: edgeProps.height }}
      onMouseDown={onBackground}
      onFocusCapture={(event) =>
        setFocusedId(
          event.target
            .closest("[data-node-id]")
            ?.getAttribute("data-node-id") ?? null
        )
      }
      onBlurCapture={(event) =>
        setFocusedId(
          event.currentTarget.contains(event.relatedTarget)
            ? (event.relatedTarget
                ?.closest("[data-node-id]")
                ?.getAttribute("data-node-id") ?? null)
            : null
        )
      }
    >
      <GraphEdges {...edgeProps} viewport={viewport} />
      <HoverEdges
        {...edgeProps}
        canvasRef={canvasRef}
        active={hoverActive}
        viewport={viewport}
      />
      {mountedNodes.map((node) => (
        <GraphNodeChip
          key={node.id}
          node={node}
          position={edgeProps.positions.get(node.id)!}
          leftVisual={visualById.get(node.id) ?? null}
          state={nodeState(node)}
          onPointerDown={onPointerDown}
          onMore={onMore}
        />
      ))}
    </Surface>
  );
}
